import { useState } from "react";
import {
  AudiencesClient,
  type AudienceExpression,
  type AudienceExplanation,
} from "../api/generated";
import { usePermissions } from "../auth/SessionContext";
import { AudienceBuilder, AudiencePreviewPanel, describeTerm } from "../components/AudienceBuilder";
import { Alert, Chip, Empty, Field, Panel, describeError, useAsync } from "../components/ui";

const audiences = new AudiencesClient("");

/**
 * Console module: <b>Audiences</b> — named audiences, preview, and explain.
 *
 * Groups are where each client's language lives: nobody composes JSON, they pick "Ontario Stores"
 * from a list (§4.6, §10).
 */
export function AudiencesPage() {
  const { hasCapability } = usePermissions();
  const canWrite = hasCapability("audiences:write");

  const schema = useAsync(() => audiences.audiences_Schema());
  const groups = useAsync(() => audiences.audiences_ListGroups());

  const [editorKey, setEditorKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function remove(key: string) {
    if (!window.confirm(`Delete audience group "${key}"?`)) return;
    setError(null);
    try {
      await audiences.audiences_DeleteGroup(key);
      await groups.reload();
      await schema.reload();
    } catch (e) {
      setError(describeError(e));
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Audiences</h1>
          <p className="sub">
            Reusable audience definitions. Preview one to see how many parties it currently matches.
          </p>
        </div>
        {canWrite && <button onClick={() => setEditorKey("")}>New audience</button>}
      </div>

      {error && <Alert kind="error">{error}</Alert>}
      {schema.error && <Alert kind="error">{schema.error}</Alert>}

      {editorKey !== null && schema.data && (
        <GroupEditor
          existingKey={editorKey}
          schema={schema.data}
          onClose={() => setEditorKey(null)}
          onSaved={async () => { setEditorKey(null); await groups.reload(); await schema.reload(); }}
        />
      )}

      <Panel title="Named audiences">
        {groups.loading ? <Empty>Loading…</Empty> : (groups.data ?? []).length === 0 ? (
          <Empty>No named audiences yet.</Empty>
        ) : (
          <table>
            <thead>
              <tr><th>Key</th><th>Name</th><th>Kind</th><th>Members</th><th>Definition</th><th></th></tr>
            </thead>
            <tbody>
              {groups.data!.map((g) => (
                <tr key={g.key}>
                  <td className="mono">{g.key}</td>
                  <td>{g.name}</td>
                  <td><Chip kind={g.kind === "dynamic" ? "on" : undefined}>{g.kind}</Chip></td>
                  <td>{g.memberCount}</td>
                  <td className="muted">
                    {g.expression
                      ? (g.expression.include ?? []).map(describeTerm).join(" OR ")
                      : "explicit list"}
                  </td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    {canWrite && <button className="ghost small" onClick={() => setEditorKey(g.key!)}>Edit</button>}{" "}
                    {canWrite && <button className="danger small" onClick={() => remove(g.key!)}>Delete</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>

      {schema.data && <ExplainPanel schema={schema.data} />}
    </>
  );
}

function GroupEditor({ existingKey, schema, onClose, onSaved }: {
  existingKey: string;
  schema: NonNullable<Awaited<ReturnType<AudiencesClient["audiences_Schema"]>>>;
  onClose: () => void;
  onSaved: () => void;
}) {
  const existing = schema.groups?.find((g) => g.key === existingKey);

  const [key, setKey] = useState(existingKey);
  const [name, setName] = useState(existing?.name ?? "");
  const [kind, setKind] = useState(existing?.kind ?? "static");
  const [partyKeys, setPartyKeys] = useState("");
  const [expression, setExpression] = useState<AudienceExpression>(
    existing?.expression ?? { include: [], require: [], exclude: [] },
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      await audiences.audiences_UpsertGroup(key, {
        key,
        name: name || key,
        kind,
        partyKeys: kind === "static"
          ? partyKeys.split(",").map((v) => v.trim()).filter(Boolean)
          : undefined,
        expression: kind === "dynamic" ? expression : undefined,
      });
      onSaved();
    } catch (e) {
      setError(describeError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel
      title={existingKey ? `Edit ${existingKey}` : "New named audience"}
      actions={<button className="ghost small" onClick={onClose}>Cancel</button>}
    >
      {error && <Alert kind="error">{error}</Alert>}

      <div className="row">
        <Field label="Key">
          <input value={key} disabled={!!existingKey} onChange={(e) => setKey(e.target.value)} placeholder="ontario-stores" />
        </Field>
        <Field label="Name"><input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ontario Stores" /></Field>
        <Field label="Kind">
          <select value={kind} onChange={(e) => setKind(e.target.value)}>
            <option value="static">static — an explicit list</option>
            <option value="dynamic">dynamic — an expression, re-resolved on change</option>
          </select>
        </Field>
      </div>

      {kind === "static" ? (
        <Field label="Party keys (comma separated)">
          <textarea value={partyKeys} onChange={(e) => setPartyKeys(e.target.value)} placeholder="p_118, p_204" />
        </Field>
      ) : (
        <>
          <AudienceBuilder schema={schema} value={expression} onChange={setExpression} />
          <div style={{ marginTop: 10 }}>
            <AudiencePreviewPanel audience={expression} />
          </div>
        </>
      )}

      <div style={{ marginTop: 12 }}>
        <button onClick={save} disabled={busy || !key}>Save audience</button>
      </div>
    </Panel>
  );
}

/** "Why did store 118 get this?" — asked in week one, and there is no delivery row to point at (§4.7). */
function ExplainPanel({ schema }: {
  schema: NonNullable<Awaited<ReturnType<AudiencesClient["audiences_Schema"]>>>;
}) {
  const [expression, setExpression] = useState<AudienceExpression>({ include: [], require: [], exclude: [] });
  const [partyKey, setPartyKey] = useState("");
  const [result, setResult] = useState<AudienceExplanation | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function run() {
    setError(null);
    try {
      setResult(await audiences.audiences_Explain({ audience: expression, partyKey }));
    } catch (e) {
      setResult(null);
      setError(describeError(e));
    }
  }

  return (
    <Panel title="Explain — why did a party match, or not?">
      <AudienceBuilder schema={schema} value={expression} onChange={setExpression} />

      <div className="row" style={{ marginTop: 10 }}>
        <Field label="Party key"><input value={partyKey} onChange={(e) => setPartyKey(e.target.value)} placeholder="p_118" /></Field>
        <button className="ghost" onClick={run} disabled={!partyKey || (expression.include ?? []).length === 0}>
          Explain
        </button>
      </div>

      {error && <div style={{ marginTop: 10 }}><Alert kind="error">{error}</Alert></div>}

      {result && (
        <div style={{ marginTop: 12 }}>
          <Alert kind={result.included ? "ok" : "info"}>
            {result.included
              ? `${result.partyKey} IS included — matched "${result.matchedTerm}" via ${result.matchedKey}.`
              : `${result.partyKey} is NOT included — responsible clause: ${result.responsibleClause}, ${result.responsibleTerm}.`}
          </Alert>

          <table style={{ marginTop: 8 }}>
            <thead><tr><th>Clause</th><th>Term</th><th>Matched</th><th>Missing facets</th></tr></thead>
            <tbody>
              {result.evaluations?.map((e, i) => (
                <tr key={i}>
                  <td>{e.clause}</td>
                  <td className="mono">{e.term}</td>
                  <td>{e.matched ? <Chip kind="ok">yes</Chip> : <Chip>no</Chip>}</td>
                  <td className="mono muted">{e.missingFacets?.join(", ") || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div style={{ marginTop: 10 }}>
            <label>Party keys</label>
            <div>{result.partyKeys?.map((k) => <Chip key={k}>{k}</Chip>)}</div>
          </div>
        </div>
      )}
    </Panel>
  );
}
